import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import { API_URL, formatDate, formatCurrency, getStatusColor, EVENT_STATUSES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Plus,
  Calendar as CalendarIcon,
  MapPin,
  User,
  Edit2,
  Trash2,
  TrendingUp,
  DollarSign,
  BarChart3,
} from 'lucide-react';

const EventCard = ({ event, onEdit, onDelete, onViewProfitability }) => {
  return (
    <Card className="card-hover" data-testid={`event-card-${event.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium">{event.title}</h3>
              <Badge variant="outline" className={getStatusColor(event.status)}>
                {EVENT_STATUSES.find((s) => s.value === event.status)?.label || event.status}
              </Badge>
            </div>

            <div className="space-y-1 text-sm text-muted-foreground">
              {event.client_name && (
                <p className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> {event.client_name}
                </p>
              )}
              {event.location && (
                <p className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {event.location}
                </p>
              )}
              {event.start_date && (
                <p className="flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {formatDate(event.start_date)}
                  {event.end_date && event.end_date !== event.start_date && ` - ${formatDate(event.end_date)}`}
                </p>
              )}
            </div>

            {event.notes && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {event.notes}
              </p>
            )}
          </div>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary"
              onClick={() => onViewProfitability(event)}
              data-testid={`profitability-event-${event.id}`}
            >
              <BarChart3 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(event)}
              data-testid={`edit-event-${event.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={() => onDelete(event.id)}
              data-testid={`delete-event-${event.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EventFormDialog = ({ event, clients, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState(
    event || {
      title: '',
      client_id: 'none',
      location: '',
      start_date: null,
      end_date: null,
      status: 'planned',
      notes: '',
    }
  );

  React.useEffect(() => {
    if (event) {
      setFormData({
        ...event,
        client_id: event.client_id || 'none',
        start_date: event.start_date ? new Date(event.start_date) : null,
        end_date: event.end_date ? new Date(event.end_date) : null,
      });
    } else {
      setFormData({
        title: '',
        client_id: 'none',
        location: '',
        start_date: null,
        end_date: null,
        status: 'planned',
        notes: '',
      });
    }
  }, [event, open]);
  
  //Fonction utilitaire pour éviter le bug du J-1 dû aux fuseaux horaires : on force les heures à midi (12:00) pour que la date envoyée soit correcte même si l'utilisateur est dans un fuseau horaire différent
  const toSafeISOString = (date) => {
    if (!date) return null;
    const safeDate = new Date(date);
    // On force l'heure à 12:00:00 (Midi) LOCALE
    safeDate.setHours(12, 0, 0, 0); 
    return safeDate.toISOString();
};
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      client_id: formData.client_id === 'none' ? null : formData.client_id,
      start_date: formData.start_date ? toSafeISOString(formData.start_date) : null,
      end_date: formData.end_date ? toSafeISOString(formData.end_date) : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {event ? 'Modifier l\'événement' : 'Nouvel événement'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titre *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="event-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Client</Label>
            <Select
              value={formData.client_id || ''}
              onValueChange={(v) => setFormData({ ...formData, client_id: v })}
            >
              <SelectTrigger data-testid="event-client-select">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans client</SelectItem>
                {clients?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Lieu</Label>
            <Input
              id="location"
              value={formData.location || ''}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date début</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="event-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.start_date
                      ? format(formData.start_date, 'dd/MM/yyyy')
                      : 'Choisir'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.start_date}
                    onSelect={(date) => setFormData({ ...formData, start_date: date })}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Date fin</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="event-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.end_date
                      ? format(formData.end_date, 'dd/MM/yyyy')
                      : 'Choisir'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.end_date}
                    onSelect={(date) => setFormData({ ...formData, end_date: date })}
                    locale={fr}
                    disabled={(date) =>
                      formData.start_date && date < formData.start_date
                    }
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {event && (
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" data-testid="event-submit-btn">
              {event ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProfitabilityDialog = ({ event, open, onOpenChange }) => {
  const { token } = useAuthStore();

  const { data: profitability, isLoading } = useQuery({
    queryKey: ['profitability', event?.id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/events/${event.id}/profitability`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: !!event && open,
  });

  const margin = profitability?.gross_margin || 0;
  const marginColor = margin >= 0 ? 'text-emerald-500' : 'text-red-500';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Rentabilité - {event?.title}
          </DialogTitle>
          <DialogDescription>
            Analyse de la marge nette HT de cet événement
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : profitability ? (
          <div className="space-y-4">
            {/* 3 KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card className="border-blue-500/20">
                <CardContent className="p-4 text-center">
                  <DollarSign className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                  <p className="text-xs text-muted-foreground">Chiffre d'Affaires HT</p>
                  <p className="text-xl font-heading font-bold text-blue-500 mt-1">
                    {formatCurrency(profitability.revenue_ht)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-orange-500/20">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-6 h-6 mx-auto text-orange-500 mb-1" />
                  <p className="text-xs text-muted-foreground">Coûts Affectés</p>
                  <p className="text-xl font-heading font-bold text-orange-500 mt-1">
                    {formatCurrency(profitability.total_expenses)}
                  </p>
                </CardContent>
              </Card>
              <Card className={margin >= 0 ? 'border-emerald-500/20' : 'border-red-500/20'}>
                <CardContent className="p-4 text-center">
                  <BarChart3 className={`w-6 h-6 mx-auto mb-1 ${marginColor}`} />
                  <p className="text-xs text-muted-foreground">Marge Nette</p>
                  <p className={`text-xl font-heading font-bold mt-1 ${marginColor}`}>
                    {formatCurrency(margin)}
                  </p>
                  <p className={`text-xs ${marginColor}`}>
                    {profitability.margin_percentage}%
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Expenses breakdown */}
            {profitability.expenses_breakdown.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Détail des coûts</h4>
                <div className="space-y-1">
                  {profitability.expenses_breakdown.map((exp) => (
                    <div key={exp.id} className="flex justify-between text-sm py-1.5 px-2 rounded bg-secondary/50">
                      <span className="text-muted-foreground">{exp.description || 'Dépense'}</span>
                      <span className="font-medium">{formatCurrency(exp.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profitability.expenses_breakdown.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucune dépense liée à cet événement.
              </p>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const EventsPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [profitabilityEvent, setProfitabilityEvent] = useState(null);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch events');
      return res.json();
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch clients');
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to create event');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Événement créé');
      setDialogOpen(false);
    },
    onError: () => toast.error('Erreur lors de la création'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update event');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Événement mis à jour');
      setDialogOpen(false);
      setEditingEvent(null);
    },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/events/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete event');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('Événement supprimé');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  const handleSave = (data) => {
    if (editingEvent) {
      updateMutation.mutate({ id: editingEvent.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredEvents = events.filter(
    (e) => statusFilter === 'all' || e.status === statusFilter
  );

  // Sort by date
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = new Date(a.start_date || 0);
    const dateB = new Date(b.start_date || 0);
    return dateA - dateB;
  });

  return (
    <div className="space-y-6" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Événements</h1>
          <p className="text-muted-foreground">Planifiez et suivez vos événements</p>
        </div>
        <Button
          onClick={() => {
            setEditingEvent(null);
            setDialogOpen(true);
          }}
          data-testid="add-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nouvel événement
        </Button>
      </div>

      {/* Filter */}
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full md:w-48" data-testid="event-status-filter">
          <SelectValue placeholder="Statut" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les statuts</SelectItem>
          {EVENT_STATUSES.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Events List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-5 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sortedEvents.length > 0 ? (
        <div className="space-y-3">
          {sortedEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onEdit={(e) => {
                setEditingEvent(e);
                setDialogOpen(true);
              }}
              onDelete={(id) => {
                if (window.confirm('Supprimer cet événement ?')) {
                  deleteMutation.mutate(id);
                }
              }}
              onViewProfitability={(e) => setProfitabilityEvent(e)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <CalendarIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Aucun événement</h3>
            <p className="text-muted-foreground mb-4">Planifiez votre premier événement</p>
            <Button
              onClick={() => {
                setEditingEvent(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Créer un événement
            </Button>
          </CardContent>
        </Card>
      )}

      <EventFormDialog
        event={editingEvent}
        clients={clients}
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingEvent(null);
        }}
        onSave={handleSave}
      />

      <ProfitabilityDialog
        event={profitabilityEvent}
        open={!!profitabilityEvent}
        onOpenChange={(open) => !open && setProfitabilityEvent(null)}
      />
    </div>
  );
};

export default EventsPage;
